import React, { useState, useEffect, useRef } from "react";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
//import { MultiSelect } from "primereact/multiselect";
import {
  fetchUserAssignedCreatedForms,
  fetchFilledforms,
} from "../UserFormsList/api";

const MultiSelectComponent = ({
  onSave,
  closeSidebar,
  fieldName,
  required,
  linked,
  database,
  field,
  options = [],
}) => {
  const [isRequired, setIsRequired] = useState(required || false);
  const [isLinked, setIsLinked] = useState(linked || false);
  const [selectedDatabase, setSelectedDatabase] = useState(database || null);
  const [showFieldsDropdown, setShowFieldsDropdown] = useState(false);
  const [selectedField, setSelectedField] = useState(field || null);
  const [fields, setFields] = useState(
    Array.isArray(options) && options.length > 0
      ? options
      : [{ option: "", value: "" }]
  );
  const [counter, setCounter] = useState(options ? options.length : 1);
  const toast = useRef(null);
  const [formsList, setFormsList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalDatabase, setModalDatabase] = useState(null);
  const [modalField, setModalField] = useState(null);
  const [filledforms, setFilledforms] = useState([]);
  // eslint-disable-next-line
  const [isLoading, setIsLoading] = useState(false);
  const [showFilledFormsModal, setShowFilledFormsModal] = useState(false);
  const [selectedFilledForm, setSelectedFilledForm] = useState(null);
  const [selectedFilledField, setSelectedFilledField] = useState(null);
  const [updateImported, setUpdateImported] = useState(false);
  const [importedFromFilled, setImportedFromFilled] = useState(false);

  const normalizeOptions = (opts) => {
    if (!Array.isArray(opts)) return [];
    return opts.flatMap((opt) => {
      if (
        opt &&
        typeof opt === "object" &&
        ("option" in opt || "value" in opt)
      ) {
        const optVals = Array.isArray(opt.option) ? opt.option : [opt.option];
        const valVals = Array.isArray(opt.value) ? opt.value : [opt.value];
        if (optVals.length === valVals.length) {
          return optVals.map((o, i) => ({
            option: String(o),
            value: String(valVals[i]),
          }));
        } else {
          return optVals.map((o, i) => ({
            option: String(o),
            value: String(valVals[i] ?? o),
          }));
        }
      } else {
        return [{ option: String(opt), value: String(opt) }];
      }
    });
  };

  useEffect(() => {
    if (Array.isArray(options) && options.length > 0) {
      const normalized = normalizeOptions(options);
      setFields(normalized.length ? normalized : [{ option: "", value: "" }]);
      setCounter(normalized.length || 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getForms = async () => {
      try {
        const forms = await fetchUserAssignedCreatedForms();
        if (forms && Array.isArray(forms)) {
          setFormsList(forms);
        } else {
          console.warn("Fetched forms is not an array or is undefined.");
        }
      } catch (error) {
        console.error("Error fetching forms:", error);
      }
    };

    getForms();
  }, []);

  useEffect(() => {
    const getFilledforms = async () => {
      setIsLoading(true);
      try {
        const filled = await fetchFilledforms();
        setFilledforms(Array.isArray(filled) ? filled : []);
      } catch (error) {
        console.error("Error fetching filled forms:", error);
        setFilledforms([]);
      } finally {
        setIsLoading(false);
      }
    };
    getFilledforms();
  }, []);

  const handleImportFilledForms = () => {
    setShowFilledFormsModal(true);
  };

  const uniqueFilledForms = Array.from(
    new Set(
      (Array.isArray(filledforms) ? filledforms : []).map(
        (form) => form.formSchemaId
      )
    )
  ).map((formSchemaId) => {
    const form = (Array.isArray(filledforms) ? filledforms : []).find(
      (form) => form.formSchemaId === formSchemaId
    );
    return {
      label: form?.formTitle ?? "",
      value: form,
    };
  });

  const handleFilledFormsModalSave = () => {
    if (selectedFilledField) {
      const raw = Array.isArray(filledforms)
        ? filledforms
            .filter(
              (form) => form.formSchemaId === selectedFilledForm.formSchemaId
            )
            .flatMap((form) =>
              Object.values(form.data)
                .filter((f) => f.key === selectedFilledField.key)
                .flatMap((f) =>
                  Array.isArray(f.value)
                    ? f.value.map((v) => ({ option: v, value: v }))
                    : [{ option: f.value, value: f.value }]
                )
            )
        : [];
      const normalized = normalizeOptions(raw);
      const final = normalized.length
        ? normalized
        : [
            { option: "Option 1", value: "option1" },
            { option: "Option 2", value: "option2" },
          ];
      setFields(final);
      setCounter(final.length);
      setImportedFromFilled(true);
    } else {
      setFields([{ option: "", value: "" }]);
      setCounter(1);
      setImportedFromFilled(false);
    }
    setShowFilledFormsModal(false);
  };

  const filterFieldsByType = (properties, allowedTypes) => {
    return Object.entries(properties).reduce((acc, [key, value]) => {
      if (allowedTypes.includes(value.type)) {
        acc[key] = value;
      }
      return acc;
    }, {});
  };

  const handleAddField = () => {
    setFields([...fields, { option: "", value: "" }]);
    setCounter(counter + 1);
  };

  const handleRemoveField = (index) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
    setCounter(counter - 1);
  };

  const handleChange = (index, event) => {
    const newFields = fields.map((f, i) => {
      if (i === index) {
        return { ...f, [event.target.name]: event.target.value };
      }
      return f;
    });
    setFields(newFields);
  };

  useEffect(() => {
    if (isLinked) {
      setShowFieldsDropdown(true);
    } else {
      setShowFieldsDropdown(false);
      setSelectedDatabase(null);
      setSelectedField(null);
    }
  }, [isLinked]);

  useEffect(() => {
    if (selectedDatabase) {
      setShowFieldsDropdown(true);
    } else {
      setShowFieldsDropdown(false);
    }
  }, [selectedDatabase]);

  const handleSave = () => {
    const validFields = fields.filter((opt) => opt.option && opt.value);
    if (validFields.length < 2) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please enter at least two options with their values.",
        life: 3000,
      });
      return;
    }

    const fieldData = {
      name: fieldName,
      type: "multiselect",
      required: isRequired,
      linked: isLinked,
      database: selectedDatabase,
      field: selectedField,
      options: validFields,
      databaseId: selectedDatabase ? selectedDatabase.id : undefined,
      updated: importedFromFilled && updateImported ? true : false,
      importedFromForm:
        importedFromFilled &&
        updateImported &&
        selectedFilledForm &&
        selectedFilledField
          ? {
              formid: selectedFilledForm.formSchemaId,
              fieldname: selectedFilledField.key,
            }
          : { formid: "", fieldname: "" },
    };

    const saveSuccessful = onSave(fieldData);
    if (saveSuccessful) {
      closeSidebar();
    }
  };

  const handleImport = () => {
    setShowModal(true);
  };

  const handleModalSave = () => {
    if (
      modalField &&
      Array.isArray(modalField.options) &&
      modalField.options.length > 0
    ) {
      const normalized = normalizeOptions(modalField.options);
      setFields(
        normalized.length > 0 ? normalized : [{ option: "", value: "" }]
      );
      setCounter(normalized.length || 1);
    } else {
      setFields([{ option: "", value: "" }]);
      setCounter(1);
    }
    setShowModal(false);
  };

  return (
    <div className="p-field">
      <Toast ref={toast} />
      <Button
        label="Import (forms)"
        onClick={handleImport}
        style={{
          backgroundColor: "#D97A07",
          border: "none",
          marginBottom: "10px",
          marginRight: "5px",
        }}
      />
      <Button
        label="Import (data)"
        onClick={handleImportFilledForms}
        style={{
          backgroundColor: "#D97A07",
          border: "none",
          marginBottom: "10px",
        }}
      />
      <Dialog
        header="Import (form)"
        visible={showModal}
        onHide={() => setShowModal(false)}
        style={{ width: "50vw" }}
      >
        <Dropdown
          filter
          value={modalDatabase}
          options={formsList.map((form) => ({
            label: form.title || form.id,
            value: form,
          }))}
          onChange={(e) => setModalDatabase(e.value)}
          placeholder="Select a Form"
          style={{ width: "100%" }}
        />
        {modalDatabase && modalDatabase.properties && (
          <Dropdown
            filter
            value={modalField}
            options={Object.entries(
              filterFieldsByType(modalDatabase.properties, [
                "dropdown",
                "checkbox",
                "radio",
                "multiselect",
              ])
            ).map(([key, value]) => ({
              label: key,
              value: value,
            }))}
            onChange={(e) => setModalField(e.value)}
            placeholder="Select a Field"
            style={{ width: "100%", marginTop: "10px" }}
          />
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "10px",
          }}
        >
          <Button
            label="Save"
            style={{ backgroundColor: "#D97A07" }}
            onClick={handleModalSave}
          />
        </div>
      </Dialog>
      <Dialog
        header="Import Filled Forms"
        visible={showFilledFormsModal}
        onHide={() => setShowFilledFormsModal(false)}
        style={{ width: "50vw" }}
      >
        <Dropdown
          filter
          value={selectedFilledForm}
          options={uniqueFilledForms}
          onChange={(e) => setSelectedFilledForm(e.value)}
          placeholder="Select a Filled Form"
          style={{ width: "100%" }}
        />
        {selectedFilledForm && selectedFilledForm.data && (
          <Dropdown
            filter
            value={selectedFilledField}
            options={Object.entries(
              filterFieldsByType(selectedFilledForm.data, [
                "dropdown",
                "checkbox",
                "radio",
                "text",
                "number",
                "email",
                "multiselect",
              ])
            ).map(([key, value]) => ({
              label: key,
              value: value,
            }))}
            onChange={(e) => setSelectedFilledField(e.value)}
            placeholder="Select a Field"
            style={{ width: "100%", marginTop: "10px" }}
          />
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "10px",
          }}
        >
          <Button
            label="Save"
            style={{ backgroundColor: "#D97A07" }}
            onClick={handleFilledFormsModalSave}
          />
        </div>
      </Dialog>
      <label className="block text-900 font-medium mb-3">
        Or set the Options yourself:
      </label>
      <div className="p-formgroup-inline mb-3">
        <div className="p-field p-col-12 p-md-6">
          <div>
            {Array.isArray(fields) &&
              fields.map((field, index) => (
                <div
                  key={index}
                  className="p-field p-grid p-align-center mb-2"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <div
                    className="p-col-5"
                    style={{ flex: "1", marginRight: "10px" }}
                  >
                    <InputText
                      type="text"
                      name="option"
                      value={field.option}
                      onChange={(e) => handleChange(index, e)}
                      placeholder="Option"
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div
                    className="p-col-5"
                    style={{ flex: "1", marginRight: "10px" }}
                  >
                    <InputText
                      type="text"
                      name="value"
                      value={field.value}
                      onChange={(e) => handleChange(index, e)}
                      placeholder="Value"
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div
                    className="p-col-2 p-text-center"
                    style={{ flex: "0 0 auto" }}
                  >
                    <Button
                      icon="pi pi-minus"
                      className="p-button mt-2"
                      onClick={() => handleRemoveField(index)}
                      style={{ backgroundColor: "#D97A07", border: "none" }}
                    />
                  </div>
                </div>
              ))}
            <div className="mt-3 text-center">
              <Button
                icon="pi pi-plus"
                className="p-button"
                onClick={handleAddField}
                style={{
                  backgroundColor: "#D97A07",
                  border: "none",
                  width: "100%",
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {selectedFilledForm && selectedFilledField && (
        <div className="p-field-checkbox mb-3">
          <label className="font-medium" htmlFor="updateImported">
            Updated Options:{" "}
          </label>
          <InputSwitch
            inputId="updateImported"
            checked={updateImported}
            onChange={(e) => setUpdateImported(e.value)}
            style={{ marginLeft: "1rem", marginTop: "0.5rem" }}
          />
        </div>
      )}
      <Divider />
      <div className="p-field-checkbox mb-3">
        <label className="font-medium" htmlFor="required">
          Required :{" "}
        </label>
        <InputSwitch
          inputId="required"
          checked={isRequired}
          onChange={(e) => setIsRequired(e.value)}
          style={{ marginLeft: "1rem", marginTop: "0.5rem" }}
        />
      </div>
      <Divider />
      <div className="p-field mb-3">
        <label htmlFor="linked" className="block text-900 font-medium mb-2">
          Link it to an existing DB :
          <InputSwitch
            checked={isLinked}
            onChange={(e) => setIsLinked(e.value)}
            style={{ marginLeft: "1rem", marginTop: "0.5rem" }}
          />
        </label>
      </div>
      {isLinked && (
        <div className="p-field mb-3">
          <Divider />
          <label
            htmlFor="databases"
            className="block text-900 font-medium mb-2"
          >
            Forms
          </label>
          <Dropdown
            filter
            id="databases"
            value={selectedDatabase}
            options={formsList.map((form) => ({
              label: form.title,
              value: form.id,
            }))}
            onChange={(e) => setSelectedDatabase(e.value)}
            placeholder="Select a Form"
            className="w-full mb-3"
          />
          {showFieldsDropdown && selectedDatabase && (
            <div className="p-field">
              <Divider />
              <label
                htmlFor="fields"
                className="block text-900 font-medium mb-2"
              >
                Select Field
              </label>
              <Dropdown
                filter
                id="fields"
                value={selectedField}
                options={
                  formsList.find((form) => form.id === selectedDatabase)
                    ?.properties
                    ? Object.keys(
                        formsList.find((form) => form.id === selectedDatabase)
                          .properties
                      ).map((key) => ({
                        label: key,
                        value: key,
                      }))
                    : []
                }
                onChange={(e) => setSelectedField(e.value)}
                placeholder="Select a Field"
                className="w-full mb-3"
              />
            </div>
          )}
        </div>
      )}
      <Button
        label="Save"
        icon="pi pi-check"
        style={{
          color: "#fff",
          backgroundColor: "#0D2E4D",
          border: "none",
          fontFamily: "poppins",
        }}
        onClick={handleSave}
      />
    </div>
  );
};

export default MultiSelectComponent;
